import axios from "axios";

// Phone push notifications (ortex/ted/ted#2485). Devices register with their FCM token,
// signed in or not; the backend links the device to the session's user when there is one.
// Every call is best-effort: a 404 (backend not deployed yet) or any other failure returns
// null and is never surfaced to the user.

const BASE = "/interface/api/push/devices";

export type PushPlatform = "ios" | "android";

export interface PushPreferences {
    newsEnabled: boolean;
    morningNewsEnabled: boolean;
    offersEnabled: boolean;
    alertsEnabled: boolean;
}

const devicePath = (token: string) => `${BASE}/${encodeURIComponent(token)}/`;

const ok = (status: number) => status >= 200 && status < 300;

export const registerPushDevice = async (token: string, platform: PushPlatform, appVersion?: string): Promise<PushPreferences | null> => {
    try {
        const res = await axios.post<PushPreferences>(`${BASE}/`, { token, platform, appVersion: appVersion ?? "" });
        return ok(res.status) ? res.data : null;
    } catch {
        return null;
    }
};

export const getPushPreferences = async (token: string): Promise<PushPreferences | null> => {
    try {
        const res = await axios.get<PushPreferences>(`${devicePath(token)}preferences/`);
        return ok(res.status) ? res.data : null;
    } catch {
        return null;
    }
};

export const updatePushPreferences = async (token: string, prefs: Partial<PushPreferences>): Promise<PushPreferences | null> => {
    try {
        const res = await axios.patch<PushPreferences>(`${devicePath(token)}preferences/`, prefs);
        return ok(res.status) ? res.data : null;
    } catch {
        return null;
    }
};

export const deletePushDevice = async (token: string): Promise<boolean> => {
    try {
        const res = await axios.delete(devicePath(token));
        return ok(res.status);
    } catch {
        return false;
    }
};

// A tapped notification, for the push stats on /staff/push/ (taps are the only per-person
// signal: neither APNs nor FCM report delivery per device). Fire and forget.
export const reportPushOpen = async (type: string, pushId?: string, token?: string | null): Promise<void> => {
    try {
        await axios.post("/interface/api/push/opened/", { type, pushId: pushId ?? "", token: token ?? "" });
    } catch {
        // stats only: never surfaced
    }
};
